/**
 * A hook to indicate when a page transition is happening.
 *
 * Can be used to render a LoadingBar.
 *
 * - Returns a status showing if we are currently loading a new page.
 * - Returns a percent to indicate how far along loading is, thereby allowing you to render a progress bar.
 *
 * @module
 */
import { useReducer, useEffect } from 'react';
import { useSelector } from '../actions/types';
import { createAction } from '../types/utils';
const UPDATE_TIME = 40;
const PROGRESS_INCREASE = 5;
const Actions = {
    start: () => createAction('start'),
    tick: () => createAction('tick'),
    finish: () => createAction('finish'),
    reset: () => createAction('reset'),
};
const initialState = {
    percent: 0,
    status: 'hidden',
};
function reducer(state, action) {
    switch (action.type) {
        case 'start': {
            return { status: 'running', percent: 0 };
        }
        case 'tick': {
            if (state.status !== 'running') {
                return state;
            }
            // Use cosine as a smoothing function
            // It could be any function to slow down progress near the ending 100%
            const smoothedProgressIncrease = Math.floor(PROGRESS_INCREASE * Math.cos(state.percent * (Math.PI / 2 / 100)));
            const newPercent = state.percent === 100
                ? state.percent
                : state.percent + smoothedProgressIncrease;
            if (state.percent === newPercent)
                return state;
            return {
                ...state,
                percent: newPercent,
            };
        }
        case 'finish':
            if (state.status === 'hidden')
                return initialState;
            return { status: 'complete', percent: 100 };
        case 'reset':
            return { status: 'hidden', percent: 0 };
    }
}
const DEFAULT_RESET_TIMEOUT_DELAY = 0;
/**
 *
 * # useLoading
 *
 * Usage example:
 *
 * ```javascript
 * import { useLoading } from '@elevio/kb-kit/lib/hooks';
 *
 * export const CustomLoader = () => {
 *   const { percent, status } = useLoading();
 *
 *   if (status === 'hidden') {
 *     return null;
 *   }
 *
 *   return (
 *     <>
 *       <label htmlFor="loader">Loading progress: </label>
 *       <progress id="loader" value={percent} max={100}>
 *         {percent} %
 *       </progress>
 *     </>
 *   );
 * };
 * ```
 *
 * @param {number} [resetTimeoutDelay=0] - Optional amount of milliseconds before loading bar is reset to initial state. Defaults to 0.
 */
export function useLoading(resetTimeoutDelay = DEFAULT_RESET_TIMEOUT_DELAY) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const loadingPage = useSelector((_state) => _state.page.loadingPage);
    const simulateProgress = () => dispatch(Actions.tick());
    useEffect(() => {
        let progressIntervalId = null;
        let finishTimeoutId = null;
        if (loadingPage) {
            dispatch(Actions.start());
            progressIntervalId = window.setInterval(simulateProgress, UPDATE_TIME);
        }
        else {
            dispatch(Actions.finish());
            new Promise((r) => setTimeout(r, resetTimeoutDelay)).then(() => dispatch(Actions.reset()));
        }
        return () => {
            progressIntervalId && clearInterval(progressIntervalId);
            finishTimeoutId && clearTimeout(finishTimeoutId);
        };
    }, [loadingPage, resetTimeoutDelay]);
    return state;
}
//# sourceMappingURL=useLoading.js.map